import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Drawer, Menu, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ExpandMore as ExpandIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { contextApi, ContextBalances, FinancialContext } from '../../services/contextApi';
import { summarizeBalances } from '../../services/balances';
import { BOT_USERNAME } from '../../config/telegram';
import { personColor, tokens } from '../../theme/tokens';

/** The groups the picker lists, in a stable order, with the color each one keeps. */
export const useGroups = () => {
  const { contexts } = useFinancialContexts();
  return useMemo(
    () => contexts.filter((context) => context.type !== 'personal' && context.isActive),
    [contexts],
  );
};

export const ContextBadge: React.FC<{ group?: FinancialContext; index?: number; size?: number }> = ({
  group,
  index = 0,
  size = 30,
}) => {
  const colors = group ? personColor(index + 1) : { color: tokens.accent, tint: tokens.accentSoft };
  return (
    <Box
      component='span'
      aria-hidden='true'
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: colors.tint,
        color: colors.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: size * 0.42,
        '& svg': { fontSize: size * 0.55 },
      }}
    >
      {group ? group.name.charAt(0).toUpperCase() : <PersonIcon />}
    </Box>
  );
};

const Option: React.FC<{
  selected: boolean;
  onClick: () => void;
  badge: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
}> = ({ selected, onClick, badge, title, subtitle, trailing }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      width: '100%',
      minHeight: 60,
      px: 1.5,
      py: 1.25,
      gap: 1.5,
      justifyContent: 'flex-start',
      textAlign: 'left',
      borderRadius: '14px',
      border: `1px solid ${selected ? tokens.accent : tokens.line}`,
      bgcolor: selected ? tokens.accentSoft : tokens.surface,
      font: 'inherit',
      color: tokens.ink,
    }}
  >
    {badge}
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box component='strong' sx={{ fontSize: 15 }}>{title}</Box>
      {subtitle && (
        <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>
          {subtitle}
        </Box>
      )}
    </Box>
    {trailing}
    {selected && <CheckIcon sx={{ color: tokens.accent }} />}
  </ButtonBase>
);

/**
 * The context in view, always at the top: "Pessoal" or a group. Opens a
 * menu on desktop and a bottom sheet on a phone; picking one switches every
 * page, since the selection is global.
 */
const ContextPicker: React.FC = () => {
  const { t, i18n } = useTranslation('navigation');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { state } = useAuth();
  const { selectedContextId, selectContext } = useFinancialContexts();
  const groups = useGroups();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [balances, setBalances] = useState<ContextBalances[] | null>(null);

  const selectedIndex = groups.findIndex((group) => group.id === selectedContextId);
  const selectedGroup = selectedIndex >= 0 ? groups[selectedIndex] : undefined;
  const open = Boolean(anchor);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    // Each group's standing is only worth a request when someone looks.
    if (balances === null && groups.length > 0) {
      contextApi
        .getAllBalances()
        .then(setBalances)
        .catch(() => setBalances([]));
    }
  };
  const close = () => setAnchor(null);
  const pick = (contextId?: string) => {
    selectContext(contextId);
    close();
  };

  const standing = useMemo(() => {
    const byContext = new Map<string, React.ReactNode>();
    if (!balances || !state.user) return byContext;
    const summary = summarizeBalances(balances, state.user.id);
    const money = (value: number, currency: string) =>
      new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(value);
    summary.contexts.forEach((context) => {
      const net = context.receivable - context.payable;
      byContext.set(
        context.contextId,
        net > 0 ? (
          <Box component='span' sx={{ color: tokens.accent, fontWeight: 700 }}>{t('shell.toReceive', { amount: money(net, context.currency) })}</Box>
        ) : net < 0 ? (
          <Box component='span' sx={{ color: tokens.warn, fontWeight: 700 }}>{t('shell.toPay', { amount: money(-net, context.currency) })}</Box>
        ) : null,
      );
    });
    return byContext;
  }, [balances, state.user, i18n.language, t]);

  const label = selectedGroup ? selectedGroup.name : t('shell.personal');

  const list = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography component='h2' sx={{ fontFamily: tokens.display, fontWeight: 700, fontSize: 19, px: 0.5, pb: 0.75 }}>
        {t('shell.contextLabel')}
      </Typography>
      <Option
        selected={!selectedGroup}
        onClick={() => pick(undefined)}
        badge={<ContextBadge size={38} />}
        title={t('shell.personal')}
        subtitle={t('shell.personalHint')}
      />
      {groups.length > 0 && (
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink3, px: 0.5, pt: 1.5, pb: 0.25 }}>
          {t('shell.groups')}
        </Typography>
      )}
      {groups.map((group, index) => (
        <Option
          key={group.id}
          selected={group.id === selectedGroup?.id}
          onClick={() => pick(group.id)}
          badge={<ContextBadge group={group} index={index} size={38} />}
          title={group.name}
          subtitle={standing.get(group.id) ?? (balances ? t('shell.settled') : undefined)}
        />
      ))}
      <ButtonBase
        onClick={() => {
          close();
          navigate('/contexts', { state: { createGroup: true } });
        }}
        sx={{
          width: '100%',
          minHeight: 54,
          px: 1.5,
          gap: 1.5,
          justifyContent: 'flex-start',
          borderRadius: '14px',
          border: `1px dashed ${tokens.line}`,
          color: tokens.accent,
          fontWeight: 600,
          font: 'inherit',
        }}
      >
        <Box component='span' sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: tokens.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AddIcon fontSize='small' />
        </Box>
        {t('shell.createGroup')}
      </ButtonBase>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink3, px: 0.5, pt: 1, lineHeight: 1.5 }}>
        {t('shell.groupHint', { bot: BOT_USERNAME })}
      </Typography>
    </Box>
  );

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup={isMobile ? 'dialog' : 'menu'}
        aria-expanded={open}
        aria-label={t('shell.openContext', { name: label })}
        sx={{
          minHeight: 44,
          maxWidth: { xs: 210, sm: 320 },
          pl: 0.75,
          pr: 1.25,
          gap: 1,
          borderRadius: '999px',
          border: `1px solid ${tokens.line}`,
          bgcolor: tokens.surface,
          font: 'inherit',
          fontWeight: 600,
          fontSize: 15,
          color: tokens.ink,
          '&:hover': { borderColor: tokens.ink3 },
        }}
      >
        <ContextBadge group={selectedGroup} index={Math.max(selectedIndex, 0)} />
        <Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
        <ExpandIcon sx={{ color: tokens.ink3, fontSize: 20 }} />
      </ButtonBase>

      {isMobile ? (
        <Drawer
          anchor='bottom'
          open={open}
          onClose={close}
          PaperProps={{ sx: { borderRadius: '24px 24px 0 0', px: 2, pt: 1.25, pb: 3.5, maxHeight: '85vh' } }}
        >
          <Box aria-hidden='true' sx={{ alignSelf: 'center', width: 40, height: 5, borderRadius: 99, bgcolor: tokens.line, mb: 1.5 }} />
          {list}
        </Drawer>
      ) : (
        <Menu
          anchorEl={anchor}
          open={open}
          onClose={close}
          MenuListProps={{ sx: { p: 1.75, width: 360 } }}
        >
          {list}
        </Menu>
      )}
    </>
  );
};

export default ContextPicker;
